import React, { useCallback, useState } from "react";
import { RemoveScroll } from "react-remove-scroll";
import { Settings as SlickSettings } from "react-slick";
import { BREAKPOINTS } from "web/react/constants";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { useInStockProductPageContext } from "web/react/pages/product/in-stock/in-stock-product-page/in-stock-product-page.context";
import { sendAnalyticsEvent } from "web/react/pages/product/product-image-gallery/utils";
import environment from "web/script/modules/environment";
import FullscreenGalleryEmo from "./__LEGACY__/product-image-gallery-fullscreen-emo";
import { Image } from "./product-gallery-image";
import { ProductImageGalleryCarouselEmo } from "./product-image-carousel-emo";
import { ProductImageGalleryFullscreen } from "./product-image-gallery-fullscreen";

export type ImageChangeEventSource = "swipe" | "arrow_click" | "thumbnail";

type ProductImageGalleryProps = {
    images: Image[];
    currentSlideIndex?: number;
    productId: string;
    buyOptionId: string;
    isInStockProductPage?: boolean;
};

export function ProductImageGallery({
    images,
    productId,
    buyOptionId,
    isInStockProductPage = false,
}: ProductImageGalleryProps): React.ReactElement {
    const [fullscreen, setFullscreen] = useState(false);
    const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);
    const [seenAll, setSeenAll] = useState(false);

    const { isMobileViewport } = useDomViewport();
    const { relatedProducts } = useInStockProductPageContext();

    const isGalleryRedesignEnabled = !!(
        isInStockProductPage && environment.getFeature("beam_web_pdp_gallery_fullscreen_redesign")
    );

    const showMoreProducts = !!(
        isGalleryRedesignEnabled &&
        isMobileViewport &&
        relatedProducts &&
        relatedProducts.length > 0
    );

    const handleImageClick = useCallback(
        (index): void => {
            setCurrentSlideIndex(index);
            setFullscreen(true);
            sendAnalyticsEvent("open_fullscreen", true, productId, buyOptionId);
        },
        [productId, buyOptionId]
    );

    const triggerSeenAll = useCallback((): void => {
        setSeenAll(true);
        sendAnalyticsEvent("seen_all", false, productId, buyOptionId);
    }, [productId, buyOptionId]);

    const onSlideChange = useCallback(
        (src: ImageChangeEventSource, nextSlide: number): void => {
            if (src === "swipe") {
                sendAnalyticsEvent("swiped", true, productId, buyOptionId);
            } else if (nextSlide !== currentSlideIndex) {
                sendAnalyticsEvent("changed_image", true, productId, buyOptionId);
            }
            setCurrentSlideIndex(nextSlide);

            if (!seenAll) {
                const lastSlideIndex = images.length - 1;

                if (nextSlide === lastSlideIndex) {
                    triggerSeenAll();
                }
            }
        },
        [currentSlideIndex, seenAll, productId, buyOptionId, images.length, triggerSeenAll]
    );

    function onSwipe(direction: "left" | "right"): void {
        const totalSlides = images.length + (showMoreProducts ? 1 : 0);
        if (
            (direction === "right" && currentSlideIndex === 0) ||
            (direction === "left" && currentSlideIndex >= totalSlides - 1)
        ) {
            return;
        }

        onSlideChange(
            "swipe",
            direction === "left" ? currentSlideIndex + 1 : currentSlideIndex - 1
        );
    }

    function onFullscreenGalleryClose(): void {
        setFullscreen(false);
        sendAnalyticsEvent("close_fullscreen", true, productId, buyOptionId);
    }

    const InPageViewSettings: SlickSettings = {
        accessibility: true,
        speed: 500,
        slidesToShow: 1,
        dots: images.length > 1,
        initialSlide: currentSlideIndex,
        afterChange: (nextSlide) => onSlideChange("arrow_click", nextSlide),
        centerMode: false,
        touchThreshold: 5,
        infinite: false,
        swipeToSlide: true,
        onSwipe: onSwipe,
        arrows: false,
        swipe: true,
        touchMove: false,
        draggable: false,
        responsive: [
            {
                breakpoint: BREAKPOINTS.Medium,
                settings: {
                    slidesToShow: 1,
                    speed: 200, // we need this for smooth swipe
                    touchMove: true,
                    draggable: false,
                },
            },
            {
                breakpoint: BREAKPOINTS.Large,
                settings: {
                    slidesToShow: 1,
                    speed: 200,
                    touchMove: true,
                    draggable: true,
                },
            },
        ],
    };

    return (
        <RemoveScroll enabled={fullscreen}>
            {fullscreen && isGalleryRedesignEnabled ? (
                <ProductImageGalleryFullscreen
                    images={images}
                    onClose={onFullscreenGalleryClose}
                    productId={productId}
                    buyOptionId={buyOptionId}
                    currentSlideIndex={currentSlideIndex}
                    onSlideChange={onSlideChange}
                    onSwipe={onSwipe}
                />
            ) : fullscreen ? (
                <FullscreenGalleryEmo
                    currentSlideIndex={currentSlideIndex}
                    isOpen={fullscreen}
                    onClose={onFullscreenGalleryClose}
                    images={images}
                    onSlideChange={onSlideChange}
                    isInStockProductPage={isInStockProductPage}
                    onSwipe={onSwipe}
                />
            ) : (
                <ProductImageGalleryCarouselEmo
                    images={images}
                    settings={InPageViewSettings}
                    isFullscreen={fullscreen}
                    handleImageClick={(index): void => handleImageClick(index)}
                    onClose={onFullscreenGalleryClose}
                    currentSlideIndex={currentSlideIndex}
                    isInStockProductPage={isInStockProductPage}
                    onSlideChange={onSlideChange}
                />
            )}
        </RemoveScroll>
    );
}
